import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import ReactLoading from 'react-loading';
import EmptyPage from '../../empty-page/emptyPage';
import { toPascalCase } from '../../../utils/pascalCase';
import {
    fetchGuideDashboardBookingsCount,
    fetchGuideDashboardBookingsData,
    fetchGuideDashboardKpiData
} from './guideDashboardFn/fetchGuideDashboardFn';
import Pagination from '../../pagination/Pagination';
import CommonTable from '../../reusable-ui-components/commonTable/CommonTable';
import { getTotalSeatCount } from '../../../utils/getTotalSeatCount';

const GuidDashboard = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [rowClickId, setRowClickId] = useState(0);
    const [loading, setLoading] = useState(false);
    const selected = searchParams.get('type') || 'next';
    const bookingsOnly = searchParams.get('bookings') || true;
    const pageNumber = Number(searchParams.get('page')) || 1;
    const [tableData, setTableData] = useState([]);
    const [kpiData, setKpiData] = useState({});
    const [totalCount, setTotalCount] = useState(0);
    const limit = 10;
    const offSet = (pageNumber - 1) * limit;
    const totalData = totalCount;
    const totalPages = Math.ceil(totalData / limit);
    const columns = [
        {
            label: 'Guide Name',
            accessor: 'guideName',
            render: (val) => val || 'N/A'
        },
        {
            label: 'Date',
            accessor: 'date',
            render: (val) => (val ? new Date(val).toLocaleDateString('en-IN') : 'N/A')
        },
        {
            label: ' Place',
            accessor: 'baseCities',
            render: (val) => val || 'N/A'
        },
        {
            label: 'Time Slot',
            accessor: 'startTime',
            render: (val, row) => {
                if (!val || !row.endTime) return '-';

                return `${val} - ${row.endTime}`;
            }
        },
        {
            label: 'Booked Seats',
            accessor: 'bookedSlots',
            render: (val, row) => (
                <div>
                    <strong>{val || 0}</strong> / {row.totalSlots || 0}
                </div>
            )
        },
        {
            label: 'Available Seats',
            accessor: 'availableSlots',
            render: (val) => val ?? 0
        },
        {
            label: 'Difficulty',
            accessor: 'difficulty',
            render: (val) => val || 'N/A'
        }
    ];

    const updateURL = useCallback(
        (params) => {
            const current = Object.fromEntries([...searchParams]);

            Object.keys(params).forEach((key) => {
                if (params[key] === '' || params[key] === null) {
                    delete current[key];
                } else {
                    current[key] = params[key];
                }
            });
            setSearchParams(current);
        },
        [searchParams, setSearchParams]
    );

    useEffect(() => {
        if (!totalPages) return;

        if (pageNumber > totalPages) {
            updateURL({ page: totalPages });
        }
        //eslint-disable-next-line
    }, [totalPages, pageNumber, updateURL]);

    useEffect(() => {
        fetchGuideDashboardKpiData(setKpiData, setLoading);
    }, []);

    useEffect(() => {
        fetchGuideDashboardBookingsCount(setTotalCount, setLoading, selected);
    }, [selected]);

    useEffect(() => {
        fetchGuideDashboardBookingsData(
            setTableData,
            setTotalCount,
            setKpiData,
            setLoading,
            selected,
            bookingsOnly,
            limit,
            offSet
        );
    }, [selected, bookingsOnly, limit, offSet]);

    if (rowClickId !== 0) {
        const tabeleDataById = tableData.find((tableDataItem) => tableDataItem?._id === rowClickId);
        const guTourId = rowClickId?.guDatesId;
        const slotId = rowClickId?.slotId;
        window.open(`/org/g/date/${guTourId}/${slotId}`);
        setRowClickId(0);
    }

    const paginationClick = (number, offSetNumber) => {
        if (Math.ceil(totalData / limit) > 1) {
            updateURL({ page: number });
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        }
    };

    return (
        <div className='dashboard'>
            {loading && (
                <div className='loadingBackground'>
                    <ReactLoading type='spin' color='#006aff' className='loadingSpin' />
                </div>
            )}
            <div className='kpi-container'>
                {Object.keys(kpiData)?.map((item, index) => (
                    <div className={`kpi-card kpi-card-${index + 1}`} key={index}>
                        <h1>{kpiData[item] === 'wallet' ? 'N/A' : kpiData[item]}</h1>
                        <h4>{toPascalCase(item)}</h4>
                    </div>
                ))}
            </div>
            <div className='DashListOfUpcoming'>
                <div className='customOptions'>
                    <div className='sticky-container'>
                        <select
                            name='date'
                            id='date'
                            className='select'
                            value={selected}
                            onChange={(e) => {
                                updateURL({ type: e.target.value, page: 1 });
                            }}
                        >
                            <option value='next'>Upcoming</option>
                            <option value='today'>Today</option>
                            <option value='previous'>Completed</option>
                        </select>
                    </div>
                    <div className='sticky-container'>
                        <select
                            name='bookingsOnly'
                            id='bookingsOnly'
                            className='select'
                            value={bookingsOnly}
                            onChange={(e) => {
                                updateURL({ bookings: e.target.value, page: 1 });
                            }}
                        >
                            <option value={false}>All Slots</option>
                            <option value={true}>Bookings Only</option>
                        </select>
                    </div>
                </div>
                {tableData?.length === 0 && !loading ? (
                    <EmptyPage />
                ) : (
                    <>
                        <CommonTable data={tableData} columns={columns} setRowClickId={setRowClickId} />
                        <Pagination
                            paginationClick={paginationClick}
                            numberOfRows={totalData}
                            offSet={offSet}
                            pageNumber={pageNumber}
                            limit={limit}
                        />
                    </>
                )}
            </div>
        </div>
    );
};

export default GuidDashboard;
